﻿/**
 * RiskTreatment Integration Layer
 * Connects SDK Service to Zustand Store
 */
import { toast } from 'sonner';
import { useRiskTreatmentStore } from '../../store/risk-treatment.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import i18next from "i18next";
import { mapColumnFiltersForQuery } from '@/lib/table-filter-query.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class RiskTreatmentIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch all items and update store
     */
    async fetchRiskTreatments(params = {}) {
        const store = useRiskTreatmentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const currentPagination = store.pagination;
            const queryParams = {
                page: params.page || currentPagination.currentPage,
                pageSize: params.pageSize || currentPagination.itemsPerPage,
                search: params.search !== undefined ? params.search : store.searchQuery,
                sortBy: params.sortBy || store.sorting.sortBy,
                sortOrder: params.sortDirection || store.sorting.sortDirection,
                ...params,
                ...mapColumnFiltersForQuery(params.columnFilters),
                tenantId
            };
            delete queryParams.columnFilters;

            if (!params.includeAllActionTypes) {
                queryParams.filter_actionType = params.filter_actionType || ['risk_treatment'];
            }

            const response = await this.service.getAll(queryParams);

            store.setRiskTreatments(response.data || []);

            // Handle Pagination Mapping & Calculation
            const paginationData = response.meta || {};
            const currentPage = parseInt(paginationData.page || params.page || 1);
            const itemsPerPage = parseInt(paginationData.pageSize || params.pageSize || 10);
            const totalItems = parseInt(paginationData.total || 0);
            const totalPages = parseInt(paginationData.totalPages || Math.ceil(totalItems / itemsPerPage) || 0);

            store.setPagination({
                currentPage,
                itemsPerPage,
                totalItems,
                totalPages,
                hasNextPage: currentPage < totalPages,
                hasPreviousPage: currentPage > 1
            });

            // Handle Unique Values
            if (response.uniqueValues) {
                store.setUniqueValues(response.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Get by ID
     */
    async fetchRiskTreatmentById(id) {
        const store = useRiskTreatmentStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.getById(id);
            store.setCurrentRiskTreatment(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Clean payload to prevent empty strings causing ObjectId CastErrors (Mongoose returns 400).
     */
    _cleanPayload(data) {
        const payload = { ...data };
        if (!payload.owner) delete payload.owner;
        if (!payload.unit) delete payload.unit;
        if (!payload.verifiedBy) delete payload.verifiedBy;
        if (!payload.startDate) delete payload.startDate;
        if (!payload.deadline) delete payload.deadline;
        if (!String(payload.verifiedDate ?? "").trim()) delete payload.verifiedDate;
        if (!String(payload.rootCause || "").trim()) delete payload.rootCause;
        delete payload.rootCauseReasons;
        if (!String(payload.preventiveRationale || "").trim()) delete payload.preventiveRationale;
        if (!String(payload.improvementObjective || "").trim()) delete payload.improvementObjective;
        for (const k of ["sourceType", "sourceId", "sourceTitle"]) {
            if (!String(payload[k] ?? "").trim()) delete payload[k];
        }
        if (typeof payload.progress === "number" && Number.isNaN(payload.progress)) {
            payload.progress = 0;
        }
        if (typeof payload.budget === "number" && Number.isNaN(payload.budget)) {
            payload.budget = 0;
        }
        return payload;
    }

    /**
     * Create item
     */
    async createRiskTreatment(data) {
        const store = useRiskTreatmentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const payload = this._cleanPayload(data);
            const response = await this.service.create({
                ...payload,
                tenantId,
                actionType: payload.actionType || 'risk_treatment'
            });

            toast.success(i18next.t('riskAnalysisRiskTreatment:messages.createSuccess', { defaultValue: 'Risk action created successfully' }));

            store.closeForm();
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update item
     */
    async updateRiskTreatment(id, data) {
        const store = useRiskTreatmentStore.getState();
        store.setLoading(true);

        try {
            const payload = this._cleanPayload(data);
            const response = await this.service.update(id, payload);

            toast.success(i18next.t('riskAnalysisRiskTreatment:messages.updateSuccess', { defaultValue: 'Risk action updated successfully' }));

            store.closeForm();
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete item
     */
    async deleteRiskTreatment(id) {
        const store = useRiskTreatmentStore.getState();
        store.setLoading(true);

        try {
            await this.service.delete(id);
            toast.success(i18next.t('integrationToasts:riskActionDeletedSuccessfully', { defaultValue: 'Risk action deleted successfully' }));
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk Delete
     */
    async bulkDeleteRiskTreatments(ids) {
        const store = useRiskTreatmentStore.getState();
        store.setLoading(true);

        try {
            await this.service.bulkDelete(ids);
            toast.success(i18next.t('integrationToasts:selectedActionsDeletedSuccessfully', { defaultValue: 'Selected actions deleted successfully' }));
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
